/**
 * 输入区域组件
 */

import React, { useRef, useCallback, useEffect, useMemo } from 'react';
import { useChatStore } from '@/stores';
import { useIsLightUi } from '@/hooks';
import { inputAreaInline, composerTextareaInline } from '@/utils/themeInline';
import { ComposerModeSelect } from './ComposerModeSelect';

interface InputAreaProps {
  onSend: (message: string) => void;
  onStop: () => void;
  onQuickAction: (action: string) => void;
  /** IndexedDB 恢复完成前禁用输入，避免与 replaceMessages 竞态 */
  storageReady?: boolean;
}

const QUICK_ACTIONS = [
  { action: 'install_bt', label: '📦 安装宝塔', command: '📦 帮我安装宝塔面板，并配置好环境' },
  { action: 'clean_garbage', label: '🧹 清理垃圾', command: '🧹 清理服务器垃圾文件，帮我释放磁盘空间' },
  { action: 'check_security', label: '🛡️ 安全检测', command: '🛡️ 检测服务器安全隐患，看看有没有漏洞或异常进程' },
  { action: 'open_ports', label: '🔓 放行端口', command: '🔓 扫描正在运行的服务并自动放行对应端口' },
];

const DEFAULT_CONTEXT_BYTES = 195 * 1024;
const AUTO_COMPRESS_RATIO = 0.8;

function estimateUtf8Bytes(text: string): number {
  if (!text) return 0;
  // 与后端 token 估算不同，这里按“上下文容量”展示，使用 UTF-8 字节近似 KB 口径。
  // 避免每次输入都 new TextEncoder；中文常见 3 bytes，ASCII 1 byte，其它字符保守按 2 bytes。
  let bytes = 0;
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    if (code <= 0x7f) bytes += 1;
    else if (code <= 0x7ff) bytes += 2;
    else if (code >= 0xd800 && code <= 0xdfff) {
      bytes += 4;
      i += 1;
    } else {
      bytes += 3;
    }
  }
  return bytes;
}

function formatContextSize(bytes: number): string {
  const kb = bytes / 1024;
  if (kb >= 1024) return `${(kb / 1024).toFixed(1)}MB`;
  if (kb >= 100) return `${Math.round(kb)}KB`;
  return `${kb.toFixed(kb >= 10 ? 0 : 1)}KB`;
}

function resolveContextCapacityBytes(): number {
  const cfg = window.sseConfig as
    | ({ context_bytes?: number; max_context_bytes?: number; context_kb?: number; max_context_kb?: number } & Record<string, unknown>)
    | undefined;
  const configuredBytes = Number(cfg?.context_bytes || cfg?.max_context_bytes || 0);
  if (Number.isFinite(configuredBytes) && configuredBytes > 32 * 1024) return configuredBytes;
  const configuredKb = Number(cfg?.context_kb || cfg?.max_context_kb || 0);
  if (Number.isFinite(configuredKb) && configuredKb > 32) return configuredKb * 1024;
  return DEFAULT_CONTEXT_BYTES;
}

function ContextUsageBadge({ usedBytes, capacityBytes }: { usedBytes: number; capacityBytes: number }) {
  const ratio = capacityBytes > 0 ? Math.min(1, usedBytes / capacityBytes) : 0;
  const percent = Math.round(ratio * 100);
  const level = percent >= 90 ? 'danger' : percent >= 70 ? 'warn' : 'ok';
  const compressAt = Math.round(capacityBytes * AUTO_COMPRESS_RATIO);
  const title = `上下文估算：已使用 ${formatContextSize(usedBytes)} / ${formatContextSize(capacityBytes)}（约 ${percent}%）。达到约 ${formatContextSize(compressAt)}（80%）后会自动压缩旧对话。`;

  return (
    <div className={`sse-context-usage sse-context-usage--${level}`} title={title} aria-label={title}>
      <span className="sse-context-usage-label">上下文</span>
      <span className="sse-context-usage-value">
        {formatContextSize(usedBytes)} / {formatContextSize(capacityBytes)}
      </span>
      <span className="sse-context-usage-auto">自动压缩</span>
      <span className="sse-context-usage-track" aria-hidden>
        <span className="sse-context-usage-fill" style={{ width: `${percent}%` }} />
        <span className="sse-context-usage-threshold" style={{ left: `${AUTO_COMPRESS_RATIO * 100}%` }} />
      </span>
    </div>
  );
}

export const InputArea: React.FC<InputAreaProps> = ({
  onSend,
  onStop,
  onQuickAction,
  storageReady = true,
}) => {
  const {
    inputValue,
    setInputValue,
    isProcessing,
    pendingMessage,
    setPendingMessage,
    messages,
    currentThinking,
    liveCommandOutput,
    streamingAnswer,
    serverContextBytes,
  } = useChatStore();
  const isLightUi = useIsLightUi();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const contextCapacityBytes = useMemo(resolveContextCapacityBytes, []);
  const usedContextBytes = useMemo(() => {
    const localHistoryBytes = messages.reduce((total, msg) => {
      const commandBlockText = msg.commandBlock
        ? `${msg.commandBlock.command}\n${msg.commandBlock.output}\n${msg.commandBlock.error || ''}`
        : '';
      return total + estimateUtf8Bytes(`${msg.role}\n${msg.content}\n${commandBlockText}`) + 64;
    }, 0);
    const historyBytes = typeof serverContextBytes === 'number' ? serverContextBytes : localHistoryBytes;
    return historyBytes +
      estimateUtf8Bytes(inputValue) +
      estimateUtf8Bytes(pendingMessage || '') +
      estimateUtf8Bytes(currentThinking || '') +
      estimateUtf8Bytes(liveCommandOutput || '') +
      estimateUtf8Bytes(streamingAnswer || '');
  }, [messages, serverContextBytes, inputValue, pendingMessage, currentThinking, liveCommandOutput, streamingAnswer]);

  // 发送后 setInputValue('') 不会触发 onChange，需同步收回内联 height（否则一直留在 240px）
  useEffect(() => {
    if (inputValue !== '') return;
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 240)}px`;
  }, [inputValue]);
  
  // 发送消息
  const handleSend = useCallback(() => {
    if (!storageReady) return;
    const message = inputValue.trim();
    if (!message) return;
    
    if (isProcessing) {
      // 如果正在处理，加入待运行队列
      setPendingMessage(message);
      setInputValue('');
      return;
    }
    
    setInputValue('');
    onSend(message);
  }, [inputValue, isProcessing, onSend, setInputValue, setPendingMessage, storageReady]);
  
  // 键盘事件
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);
  
  // 输入变化
  const handleInput = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
    
    // 自动调整高度
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 240) + 'px';
    }
  }, [setInputValue]);
  
  // 快捷操作
  const handleQuickAction = useCallback((action: string) => {
    const cmd = QUICK_ACTIONS.find((a) => a.action === action)?.command;
    if (cmd) {
      onQuickAction(cmd);
    }
  }, [onQuickAction]);
  
  // 点击待运行消息
  const handlePendingClick = useCallback(() => {
    if (pendingMessage) {
      onStop();
      // 发送待运行消息的逻辑在父组件处理
    }
  }, [pendingMessage, onStop]);
  
  return (
    <div className="sse-ai-input-area" style={inputAreaInline(isLightUi)}>
      {/* 待运行消息条 */}
      {pendingMessage && (
        <div className="sse-ai-pending-bar" onClick={handlePendingClick} title="点击立即发送新任务">
          <span className="pending-icon">
            <span className="dashicons dashicons-arrow-up-alt2"></span>
          </span>
          <span className="pending-text">待运行</span>
          <span className="pending-message">
            {pendingMessage.length > 30 ? pendingMessage.substring(0, 30) + '...' : pendingMessage}
          </span>
        </div>
      )}
      
      {/* Cursor 式：上方多行输入 + 下方工具栏（Agent + 发送），不叠层 */}
      <div className="sse-ai-input-wrapper">
        <div className="sse-ai-input-composer">
          <textarea
            ref={textareaRef}
            className="sse-ai-input sse-ai-input--composer"
            rows={2}
            value={inputValue}
            onChange={handleInput}
            onKeyDown={handleKeyDown}
            disabled={!storageReady || (isProcessing && !pendingMessage)}
            placeholder={storageReady ? '输入问题或命令…' : '正在加载会话…'}
            style={composerTextareaInline(isLightUi)}
          />
          <div className="sse-ai-input-inner-foot">
            <ComposerModeSelect
              compact
              menuUp
              disabled={!storageReady || isProcessing}
            />
            <ContextUsageBadge
              usedBytes={usedContextBytes}
              capacityBytes={contextCapacityBytes}
            />
            <div className="sse-ai-input-foot-actions">
              {isProcessing ? (
                <button
                  type="button"
                  className="sse-ai-input-foot-stop"
                  title="停止"
                  onClick={onStop}
                >
                  <span className="dashicons dashicons-no-alt" aria-hidden />
                </button>
              ) : (
                <button
                  type="button"
                  className="sse-cursor-composer-submit sse-ai-input-composer-send"
                  title="发送"
                  aria-label="发送"
                  onClick={handleSend}
                  disabled={!storageReady || !inputValue.trim()}
                >
                  <span className="dashicons dashicons-arrow-up-alt2" aria-hidden />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* 快捷操作 */}
      <div className="sse-ai-quick-actions">
        {QUICK_ACTIONS.map((action) => (
          <button
            key={action.action}
            className="sse-ai-quick-btn"
            onClick={() => handleQuickAction(action.action)}
            disabled={!storageReady || isProcessing}
          >
            {action.label}
          </button>
        ))}
      </div>
    </div>
  );
};